import React, { useEffect, useState, useRef } from 'react';
import { TutorialStep, TutorialAnimation } from '../../types/tutorial';

interface TutorialOverlayProps {
  step: TutorialStep;
  totalSteps: number;
  currentStepIndex: number;
  initialAnimation?: TutorialAnimation;
  onNext: () => void;
  onPrevious: () => void;
  onSkip: () => void;
  onDismiss: () => void;
}

interface OverlayPosition {
  top: number;
  left: number;
  arrowPosition?: 'top' | 'bottom' | 'left' | 'right';
  arrowOffset?: number; // Offset from center in pixels
}

const TutorialOverlay: React.FC<TutorialOverlayProps> = ({
  step,
  totalSteps,
  currentStepIndex,
  initialAnimation,
  onNext,
  onPrevious,
  onSkip,
  onDismiss,
}) => {
  const [position, setPosition] = useState<OverlayPosition | null>(null);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [backdropVisible, setBackdropVisible] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);

  // Show backdrop once on mount
  useEffect(() => {
    setBackdropVisible(true);
  }, []);

  // Calculate position based on target element
  useEffect(() => {
    setIsVisible(false);
    // Keep old position for smooth transition, don't set to null

    if (!step.targetSelector) {
      // Center position - delay to ensure overlay is rendered
      const timer = setTimeout(() => {
        const overlayWidth = overlayRef.current?.offsetWidth || 320;
        const overlayHeight = overlayRef.current?.offsetHeight || 200;

        setPosition({
          top: (window.innerHeight - overlayHeight) / 2,
          left: (window.innerWidth - overlayWidth) / 2,
        });
        setTargetRect(null);

        // Set visible after a short delay to allow transition
        setTimeout(() => setIsVisible(true), 50);
      }, 50);

      return () => clearTimeout(timer);
    }

    // If step has an action (like scrolling), wait longer for it to complete
    const delayTime = step.action ? 450 : 50;

    const timer = setTimeout(() => {
      if (!step.targetSelector) return;

      const targetElement = document.querySelector(step.targetSelector);
      if (!targetElement) {
        setIsVisible(true);
        return;
      }

      const rect = targetElement.getBoundingClientRect();
      setTargetRect(rect);

      // Wait for content to render, then get actual dimensions and reposition
      setTimeout(() => {
        const overlayWidth = overlayRef.current?.offsetWidth || 320;
        const overlayHeight = overlayRef.current?.offsetHeight || 200;
        const spacing = 20;

        let top = 0;
        let left = 0;
        let arrowPosition: 'top' | 'bottom' | 'left' | 'right' = 'top';

        switch (step.placement) {
          case 'top':
            top = rect.top - overlayHeight - spacing;
            left = rect.left + rect.width / 2 - overlayWidth / 2;
            arrowPosition = 'bottom';
            break;
          case 'bottom':
            top = rect.bottom + spacing;
            left = rect.left + rect.width / 2 - overlayWidth / 2;
            arrowPosition = 'top';
            break;
          case 'left':
            // Align center of overlay with center of target element
            top = rect.top + rect.height / 2 - overlayHeight / 2;
            left = rect.left - overlayWidth - spacing;
            arrowPosition = 'right';
            break;
          case 'right':
            // Align center of overlay with center of target element
            top = rect.top + rect.height / 2 - overlayHeight / 2;
            left = rect.right + spacing;
            arrowPosition = 'left';
            break;
          case 'center':
            top = window.innerHeight / 2 - overlayHeight / 2;
            left = window.innerWidth / 2 - overlayWidth / 2;
            break;
        }

        // Apply custom offsets
        if (step.offsetX) left += step.offsetX;
        if (step.offsetY) top += step.offsetY;

        // Calculate arrow offset before constraining to viewport
        const targetCenterX = rect.left + rect.width / 2;
        const targetCenterY = rect.top + rect.height / 2;

        // Keep within viewport bounds
        const margin = 10;
        const constrainedTop = Math.max(
          margin,
          Math.min(top, window.innerHeight - overlayHeight - margin)
        );
        const constrainedLeft = Math.max(
          margin,
          Math.min(left, window.innerWidth - overlayWidth - margin)
        );

        // Calculate arrow offset based on how much we had to constrain the position
        let arrowOffset = 0;
        const maxArrowOffset = 100; // Maximum distance arrow can move from center

        if (step.placement === 'left' || step.placement === 'right') {
          // For left/right, arrow moves vertically
          const finalOverlayCenterY = constrainedTop + overlayHeight / 2;
          const unclamped = targetCenterY - finalOverlayCenterY;
          arrowOffset = Math.max(
            -maxArrowOffset,
            Math.min(maxArrowOffset, unclamped)
          );
        } else if (step.placement === 'top' || step.placement === 'bottom') {
          // For top/bottom, arrow moves horizontally
          const finalOverlayCenterX = constrainedLeft + overlayWidth / 2;
          const unclamped = targetCenterX - finalOverlayCenterX;
          arrowOffset = Math.max(
            -maxArrowOffset,
            Math.min(maxArrowOffset, unclamped)
          );
        }

        setPosition({
          top: constrainedTop,
          left: constrainedLeft,
          arrowPosition:
            step.placement !== 'center' ? arrowPosition : undefined,
          arrowOffset,
        });
        setIsVisible(true);
      }, 100); // Wait for content to render
    }, delayTime);

    return () => clearTimeout(timer);
  }, [step]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onSkip();
      } else if (e.key === 'Enter') {
        if (currentStepIndex < totalSteps - 1) {
          onNext();
        } else {
          onDismiss();
        }
      } else if (e.key === 'ArrowLeft' && currentStepIndex > 0) {
        onPrevious();
      } else if (e.key === 'ArrowRight' && currentStepIndex < totalSteps - 1) {
        onNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentStepIndex, totalSteps, onNext, onPrevious, onSkip, onDismiss]);

  const handleBackdropClick = () => {
    onSkip();
  };

  const getArrowStyle = () => {
    if (!position) return { className: '', style: {} };

    const offset = position.arrowOffset || 0;
    const baseClasses = 'absolute w-0 h-0';

    switch (position.arrowPosition) {
      case 'top':
        return {
          className: `${baseClasses} -top-2 border-l-8 border-l-transparent border-r-8 border-r-transparent border-b-8 border-b-white dark:border-b-gray-800`,
          style: {
            left: `calc(50% + ${offset}px)`,
            transform: 'translateX(-50%)',
          },
        };
      case 'bottom':
        return {
          className: `${baseClasses} -bottom-2 border-l-8 border-l-transparent border-r-8 border-r-transparent border-t-8 border-t-white dark:border-t-gray-800`,
          style: {
            left: `calc(50% + ${offset}px)`,
            transform: 'translateX(-50%)',
          },
        };
      case 'left':
        return {
          className: `${baseClasses} -left-2 border-t-8 border-t-transparent border-b-8 border-b-transparent border-r-8 border-r-white dark:border-r-gray-800`,
          style: {
            top: `calc(50% + ${offset}px)`,
            transform: 'translateY(-50%)',
          },
        };
      case 'right':
        return {
          className: `${baseClasses} -right-2 border-t-8 border-t-transparent border-b-8 border-b-transparent border-l-8 border-l-white dark:border-l-gray-800`,
          style: {
            top: `calc(50% + ${offset}px)`,
            transform: 'translateY(-50%)',
          },
        };
      default:
        return { className: baseClasses, style: {} };
    }
  };

  // Don't render until position is calculated
  if (!position) return null;

  return (
    <>
      {/* Backdrop with cutout */}
      {targetRect ? (
        <>
          {/* Overlay with cutout using SVG mask for rounded corners */}
          <svg
            className={`fixed inset-0 z-[9998] transition-opacity duration-300 ${
              backdropVisible ? 'opacity-100' : 'opacity-0'
            }`}
            style={{ width: '100%', height: '100%' }}
            onClick={handleBackdropClick}
          >
            <defs>
              <mask id="tutorial-mask">
                {/* White rectangle covering entire screen */}
                <rect x="0" y="0" width="100%" height="100%" fill="white" />
                {/* Black rounded rectangle to cut out the highlighted area */}
                <rect
                  x={targetRect.left - 8}
                  y={targetRect.top - 8}
                  width={targetRect.width + 16}
                  height={targetRect.height + 16}
                  rx="12"
                  ry="12"
                  fill="rgb(64, 64, 64)"
                  opacity="0.8"
                />
              </mask>
            </defs>
            {/* Semi-transparent overlay with cutout */}
            <rect
              x="0"
              y="0"
              width="100%"
              height="100%"
              fill="rgba(0, 0, 0, 0.5)"
              mask="url(#tutorial-mask)"
            />
          </svg>
        </>
      ) : (
        /* Simple backdrop when no target element */
        <div
          className={`fixed inset-0 z-[9998] bg-black/50 transition-opacity duration-300 ${
            backdropVisible ? 'opacity-100' : 'opacity-0'
          }`}
          onClick={handleBackdropClick}
        />
      )}

      {/* Tutorial content */}
      <div
        ref={overlayRef}
        className={`fixed z-[10000] bg-white dark:bg-gray-800 rounded-lg shadow-2xl p-6 max-w-sm transition-all duration-300 ${
          isVisible
            ? 'opacity-100 translate-x-0 translate-y-0'
            : currentStepIndex === 0 && initialAnimation
              ? (() => {
                  switch (initialAnimation) {
                    case 'top-left':
                      return 'opacity-0 -translate-x-8 -translate-y-8';
                    case 'top':
                      return 'opacity-0 -translate-y-8';
                    case 'top-right':
                      return 'opacity-0 translate-x-8 -translate-y-8';
                    case 'bottom':
                      return 'opacity-0 translate-y-8';
                    case 'left':
                      return 'opacity-0 -translate-x-8';
                    case 'right':
                      return 'opacity-0 translate-x-8';
                    default:
                      return 'opacity-0 scale-95';
                  }
                })()
              : 'opacity-0 scale-95'
        }`}
        style={{
          top: position.top,
          left: position.left,
        }}
      >
        {/* Arrow */}
        {position.arrowPosition &&
          (() => {
            const arrow = getArrowStyle();
            return <div className={arrow.className} style={arrow.style} />;
          })()}

        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {step.title}
          </h3>
          <button
            onClick={onDismiss}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            aria-label="Skip tutorial"
            title="Skip tutorial"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div
          className="text-gray-600 dark:text-gray-400 mb-6 text-sm leading-relaxed prose prose-sm dark:prose-invert max-w-none [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mt-2"
          dangerouslySetInnerHTML={{ __html: step.content }}
        />

        {/* Progress dots */}
        <div className="flex items-center justify-center gap-2 mb-4">
          {Array.from({ length: totalSteps }).map((_, index) => (
            <div
              key={index}
              className={`w-2 h-2 rounded-full transition-all ${
                index === currentStepIndex
                  ? 'bg-blue-500 w-3'
                  : index < currentStepIndex
                    ? 'bg-blue-300 dark:bg-blue-700'
                    : 'bg-gray-300 dark:bg-gray-600'
              }`}
            />
          ))}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end">
          {currentStepIndex > 0 && (
            <button
              onClick={onPrevious}
              className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors mr-auto"
            >
              Previous
            </button>
          )}

          <button
            onClick={onNext}
            className="px-4 py-2 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            {currentStepIndex < totalSteps - 1 ? 'Next' : 'Got it!'}
          </button>
        </div>
      </div>
    </>
  );
};

export default TutorialOverlay;
